<template>
  <div id="root">
    <el-row type="flex" class="row-bg">
      <el-col>
        <div class="grid-content bg-purple">
          <h2>{{ title }}</h2>
        </div>
      </el-col>
    </el-row>

    <!-- Location selector and main action buttons row -->
    <el-row v-if="!loading && !error">
      <el-col style="padding-left: 0;">
        <div class="grid-content button-row">
          <!-- Location selector for users with multiple location access -->
          <el-select
            v-if="isOneOfTheseRoles(['regional'])"
            class="select-default col-md-4"
            filterable
            clearable
            v-model="locationId"
            placeholder="Select location..."
            style="margin:10px;"
          >
            <el-option
              v-for="item in locations"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>

          <!-- Main action buttons -->
          <el-button
            class="button--gospel"
            @click.native="modals.gospel = true"
            v-if="isOneOfTheseRoles(invitationAllowedRoles)"
          >
            Log a Gospel Presentation <i class="el-icon-plus el-icon--right" />
          </el-button>
          <el-button
            class="button--highCall"
            @click.native="modals.highCall = true"
            v-if="isOneOfTheseRoles(invitationAllowedRoles)"
          >
            Log a High Call <i class="el-icon-plus el-icon--right" />
          </el-button>
        </div>
      </el-col>
    </el-row>

    <!-- Filter row -->
    <el-row v-if="!loading && !error" style="margin-bottom: 10px; margin-left: -20px;">
      <el-col style="padding-left: 0;">
        <div class="grid-content button-row">
          <!-- Consolidated multi-select filters -->
          <el-select
            class="select-default col-md-3 inline-filter-select"
            multiple
            v-model="selectedGpFilters"
            placeholder="Gospel Presentation filters..."
            style="margin:10px;"
          >
            <el-option-group label="Status">
              <el-option label="Gospel Presentation Received" value="gp_received_yes" />
              <el-option label="Gospel Presentation Not Received" value="gp_received_no" />
            </el-option-group>
            <el-option-group label="Response">
              <el-option label="Yes" value="gp_response_yes" />
              <el-option label="No" value="gp_response_no" />
            </el-option-group>
          </el-select>

          <el-select
            class="select-default col-md-3 inline-filter-select"
            multiple
            v-model="selectedHcFilters"
            placeholder="High Call filters..."
            style="margin:10px;"
          >
            <el-option-group label="Status">
              <el-option label="High Call Received" value="hc_received_yes" />
              <el-option label="High Call Not Received" value="hc_received_no" />
            </el-option-group>
            <el-option-group label="Response">
              <el-option label="Green" value="hc_response_green" />
              <el-option label="Yellow" value="hc_response_yellow" />
              <el-option label="Red" value="hc_response_red" />
            </el-option-group>
          </el-select>

          <el-tooltip
            content="This reflects the student's current involvement."
            placement="top"
          >
            <el-select
              clearable
              class="select-default col-md-2 inline-filter-select"
              v-model="selectedInvolvements"
              placeholder="Involvement filters..."
              style="margin:10px;"
            >
              <el-option
                v-for="involvement in involvements"
                :key="involvement.id"
                :label="involvement.name"
                :value="involvement.name"
              />
            </el-select>
          </el-tooltip>

          <!-- Date type selector -->
          <el-select
            class="select-default col-md-2 inline-filter-select"
            v-model="dateFilterType"
            placeholder="Date filters..."
            clearable
            style="margin:10px;"
          >
            <el-option label="Gospel Presentation" value="gp" />
            <el-option label="High Call" value="hc" />
          </el-select>
        </div>
      </el-col>
    </el-row>

    <!-- Date picker row - only show when date type is selected -->
    <el-row v-if="!loading && !error && dateFilterType">
      <el-col style="padding-left: 0;">
        <div class="grid-content button-row" style="justify-content: flex-end;">
          <el-date-picker
            class="col-md-3"
            v-model="dateRange"
            type="daterange"
            range-separator="-"
            start-placeholder="Date From"
            end-placeholder="Date To"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            clearable
            @change="handleDateChange"
          />
        </div>
      </el-col>
    </el-row>

    <div
      v-loading="loading"
      v-if="loading"
      element-loading-text="Loading invitations..."
    ></div>

    <el-row v-if="!loading">
      <el-col>
        <h3 v-if="error">
          <!-- {{ error }} -->
          An error ocurred. Try reloading the page or confirming that you were added to Discipleship by a staff member.
        </h3>
        <div v-if="!error">
          <PaginatedSearchTable
            v-if="!error"
            :data="people"
            :parent-namespace="namespace"
            @updateTable="getInvitationTable"
          >
            <template v-slot:search>
              <fg-input
                class="input-sm"
                @keyup.enter="updateSearch"
                placeholder="Search"
                v-model="searchQuery"
              />
            </template>
            <el-table-column
              label="Name"
              min-width="200"
            >
              <template slot-scope="scope">
                <a
                  href="#"
                  @click.prevent="show(scope.$index)"
                  class="invitation-link"
                  style="color: #409EFF; cursor: pointer;"
                >
                  {{ scope.row.name }}
                </a>
              </template>
            </el-table-column>
            <el-table-column
              label="Gospel Presentation"
              min-width="300"
            >
              <template slot="header">
                <span>Gospel Presentation</span>
                <span
                  style="margin-left: 5px; display: inline-flex; align-items: center; cursor: pointer;"
                  @click="handleSort('gospel_presentation_date')"
                >
                  <i
                    class="el-icon-date"
                    style="color: #909399; font-size: 16px; margin-right: 2px;"
                  />
                  <i
                    v-if="sortBy === 'gospel_presentation_date'"
                    :class="sortOrder === 'asc' ? 'el-icon-top' : 'el-icon-bottom'"
                    :style="{ color: '#409EFF', fontSize: '18px' }"
                  />
                  <i
                    v-else
                    class="el-icon-top"
                    style="color: #C0C4CC; font-size: 18px; opacity: 0.3;"
                  />
                </span>
              </template>
              <template slot-scope="scope">
                <div v-if="scope.row.hasReceivedGospel === 'True'">
                  <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                    <div style="flex: 1;">
                      <strong>{{ scope.row.gospelPresentationResponse || 'No Response' }}</strong>
                      <br>
                      <small v-if="scope.row.gospelPresentationDate">{{ scope.row.gospelPresentationDate }}</small>
                      <br>
                      <small v-if="scope.row.gospel_presenter" style="color: #909399;">
                        Presenter: {{ scope.row.gospel_presenter }}
                      </small>
                      <div
                        v-if="scope.row.gospelPresentationNotes && !isOneOfTheseRoles(['student'])"
                        style="margin-top: 5px;"
                      >
                        <small style="font-style: italic;">{{ scope.row.gospelPresentationNotes }}</small>
                      </div>
                    </div>
                    <div style="margin-left: 10px;" v-if="isOneOfTheseRoles(invitationAllowedRoles)">
                      <el-button
                        type="success"
                        size="mini"
                        icon="el-icon-edit"
                        circle
                        @click="editGospelPresentation(scope.row)"
                        style="margin-bottom: 2px;"
                      />
                      <br>
                      <el-button
                        type="danger"
                        size="mini"
                        icon="el-icon-delete"
                        circle
                        @click="deleteGospelPresentation(scope.row)"
                      />
                    </div>
                  </div>
                </div>
                <div v-else style="color: #909399;">
                  Not received
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="High Call"
              min-width="300"
            >
              <template slot="header">
                <span>High Call</span>
                <span
                  style="margin-left: 5px; display: inline-flex; align-items: center; cursor: pointer;"
                  @click="handleSort('high_call_date')"
                >
                  <i
                    class="el-icon-date"
                    style="color: #909399; font-size: 16px; margin-right: 2px;"
                  />
                  <i
                    v-if="sortBy === 'high_call_date'"
                    :class="sortOrder === 'asc' ? 'el-icon-top' : 'el-icon-bottom'"
                    :style="{ color: '#409EFF', fontSize: '18px' }"
                  />
                  <i
                    v-else
                    class="el-icon-top"
                    style="color: #C0C4CC; font-size: 18px; opacity: 0.3;"
                  />
                </span>
              </template>
              <template slot-scope="scope">
                <div v-if="scope.row.hasReceivedHighCall === 'True'">
                  <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                    <div style="flex: 1;">
                      <strong
                        :style="getHighCallResponseStyle(scope.row.highCallResponse)"
                      >
                        {{ scope.row.highCallResponse || 'No Response' }}
                      </strong>
                      <br>
                      <small v-if="scope.row.highCallDate">{{ scope.row.highCallDate }}</small>
                      <br>
                      <small v-if="scope.row.high_call_presenter" style="color: #909399;">
                        Presenter: {{ scope.row.high_call_presenter }}
                      </small>
                      <div v-if="scope.row.highCallNotes && !isOneOfTheseRoles(['student'])" style="margin-top: 5px;">
                        <small style="font-style: italic;">{{ scope.row.highCallNotes }}</small>
                      </div>
                    </div>
                    <div style="margin-left: 10px;" v-if="isOneOfTheseRoles(invitationAllowedRoles)">
                      <el-button
                        type="success"
                        size="mini"
                        icon="el-icon-edit"
                        circle
                        @click="editHighCall(scope.row)"
                        style="margin-bottom: 2px;"
                      />
                      <br>
                      <el-button
                        type="danger"
                        size="mini"
                        icon="el-icon-delete"
                        circle
                        @click="deleteHighCall(scope.row)"
                      />
                    </div>
                  </div>
                </div>
                <div v-else style="color: #909399;">
                  Not received
                </div>
              </template>
            </el-table-column>
          </PaginatedSearchTable>
        </div>

        <gospel-presentations-modal
          :show="modals.gospel"
          :editing-data="editMode.gospel ? editingData : null"
          @close="closeGospelModal"
          @discipleChange="getInvitationTable"
        />
        <high-call-modal
          :show="modals.highCall"
          :editing-data="editMode.highCall ? editingData : null"
          @close="closeHighCallModal"
          @discipleChange="getInvitationTable"
        />
        <!-- Delete Confirmation Modal -->
        <el-dialog
          :visible.sync="modals.deleteConfirm"
          width="30%"
        >
          <p>
            Are you sure you want to remove this
            {{ deleteTarget && deleteTarget.type === 'gospel' ? 'Gospel Presentation' : 'High Call' }}?
          </p>
          <p><strong>This should only be done if it was logged in error.</strong></p>
          <span slot="footer" class="dialog-footer">
            <el-button @click="modals.deleteConfirm = false">Cancel</el-button>
            <el-button type="danger" @click="confirmDelete">Delete</el-button>
          </span>
        </el-dialog>
      </el-col>
    </el-row>
  </div>
</template>

<script>

import { mapState, mapGetters } from 'vuex';
import PaginatedSearchTable from '../../components/Shared/PaginatedSearchTable';
import fgInput from '../../components/UIComponents/Inputs/formGroupInput.vue';
import GospelPresentationsModal from '../../components/Invitations/GospelPresentationsModal.vue';
import HighCallModal from '../../components/Invitations/HighCallModal.vue';
import apiService from '../../repositories/apiService';

export default {
    components: {
        PaginatedSearchTable,
        fgInput,
        GospelPresentationsModal,
        HighCallModal
    },
    data() {
        return {
            title: 'Invitations',
            namespace: 'People',
            searchQuery: '',
            selectedGpFilters: [],
            selectedHcFilters: [],
            selectedInvolvements: null,
            dateRange: null,
            dateFilterType: null,
            sortBy: null,
            sortOrder: 'desc',
            filters: {
                receivedGp: null,
                receivedHc: null,
                gpResponse: null,
                hcResponse: null,
            },
            loading: false,
            modals: {
                gospel: false,
                highCall: false,
                deleteConfirm: false
            },
            editMode: {
                gospel: false,
                highCall: false
            },
            editingData: null,
            deleteTarget: null,
            itemsPerPage: 10,
            invitationAllowedRoles: [
                'student',
                'missionary',
                'td',
                'chaplain',
                'parish-missionary',
                'parish-td',
                'affiliate'
            ],
            involvements: [
                { id: 'na', name: 'N/A' },
                { id: 'bspx1', name: 'BSPx1' },
                { id: 'bspx4', name: 'BSPx4' },
                { id: 'growing_disciple', name: 'Growing Disciple' },
                { id: 'commissioned_disciple', name: 'Commissioned Disciple' },
                { id: 'slbs', name: 'SLBS' },
                { id: 'growing_disciple_maker', name: 'Growing Disciple Maker' },
                { id: 'disciple_maker', name: 'Disciple Maker' }
            ],
        };
    },
    computed: {
        ...mapState({
            people: (state) => state.People.people,
            error: (state) => state.People.error,
            locations: (state) => state.userLocations
        }),
        ...mapGetters({
            locationFromStore: 'lastLocation'
        }),
        locationId: {
            get() {
                return this.locationFromStore;
            },
            set(newLocation) {
                this.$store.commit('setLastLocation', newLocation);
                this.getInvitationTable();
            }
        },
        // Sync with pagination store's search query
        paginationSearchQuery() {
            return this.$store.state.People.Pagination.searchQuery;
        }
    },
    methods: {
        getHighCallResponseStyle(response) {
            if (!response) return { color: '#909399' }; // Gray for no response

            switch (response.toLowerCase()) {
            case 'green':
                return {
                    color: '#67C23A',
                    backgroundColor: '#f0f9e8',
                    padding: '2px 6px',
                    borderRadius: '3px'
                }; // Muted green
            case 'yellow':
                return {
                    color: '#E6A23C',
                    backgroundColor: '#fdf8f0',
                    padding: '2px 6px',
                    borderRadius: '3px'
                }; // Muted amber
            case 'red':
                return {
                    color: '#F56C6C',
                    backgroundColor: '#fef0f0',
                    padding: '2px 6px',
                    borderRadius: '3px'
                }; // Muted red
            default:
                return { color: '#909399' }; // Default gray
            }
        },
        updateSearch() {
            // Reset to page 1 when searching
            this.$store.commit('People/Pagination/setCurrentPage', 1);
            // Sync the pagination store's search query
            this.$store.commit('People/Pagination/setSearch', this.searchQuery);
            this.$store.dispatch('People/Pagination/searchUpdated', {
                search: this.searchQuery
            }).then(() => {
                this.getInvitationTable();
            });
        },
        getInvitationTable() {
            this.loading = true;
            const payload = {
                filters: this.filters,
                search: this.searchQuery,
                locationId: this.locationId
            };

            // Only add sort parameters if user has explicitly chosen a sort
            if (this.sortBy) {
                payload.sortBy = this.sortBy;
                payload.sortOrder = this.sortOrder;
            }

            // Add date filters if they exist
            if (this.dateRange && this.dateRange.length === 2 && this.dateFilterType) {
                const [dateFrom, dateTo] = this.dateRange;
                if (this.dateFilterType === 'gp') {
                    payload.gpDateFrom = dateFrom;
                    payload.gpDateTo = dateTo;
                } else if (this.dateFilterType === 'hc') {
                    payload.hcDateFrom = dateFrom;
                    payload.hcDateTo = dateTo;
                }
            }

            this.$store.dispatch('People/getInvitationTable', payload)
                .then(() => { this.loading = false; })
                .catch(() => { this.loading = false; });
        },
        handleDateChange(value) {
            this.dateRange = value;
            // Only reload if a date type is selected and we have a date range
            if (this.dateFilterType && value && value.length === 2) {
                // Reset to page 1 when filter changes
                this.$store.commit('People/Pagination/setCurrentPage', 1);
                this.getInvitationTable();
            } else if (!value && this.dateFilterType) {
                // If date range is cleared but type is still selected, reload to remove filter
                this.$store.commit('People/Pagination/setCurrentPage', 1);
                this.getInvitationTable();
            }
        },
        handleSort(field) {
            // If clicking the same field that's already sorted
            if (this.sortBy === field) {
                // If descending, switch to ascending
                if (this.sortOrder === 'desc') {
                    this.sortOrder = 'asc';
                } else {
                    // If ascending, clear the sort (go back to default)
                    this.sortBy = null;
                    this.sortOrder = 'desc';
                }
            } else {
                // New field, set to descending
                this.sortBy = field;
                this.sortOrder = 'desc';
            }
            this.$store.commit('People/Pagination/setCurrentPage', 1);
            this.getInvitationTable();
        },
        editGospelPresentation(row) {
            this.editMode.gospel = true;
            this.editingData = {
                id: row.id,
                receiverPersonId: row.id, // The row ID represents the receiver
                presenterPersonId: row.gospel_presenter_person_id || null,
                gospelPresentationDate: row.gospelPresentationDate
                    ? new Date(`${row.gospelPresentationDate}T00:00:00`)
                    : null,
                gospelPresentationResponse: row.gospelPresentationResponse,
                gospelPresentationNotes: row.gospelPresentationNotes,
                presenter: row.gospel_presenter,
                receiver: row.name,
            };
            this.modals.gospel = true;
        },
        editHighCall(row) {
            this.editMode.highCall = true;
            this.editingData = {
                id: row.id,
                receiverPersonId: row.id, // The row ID represents the receiver
                presenterPersonId: row.high_call_presenter_person_id || null,
                highCallDate: row.highCallDate
                    ? new Date(`${row.highCallDate}T00:00:00`)
                    : null,
                highCallResponse: row.highCallResponse,
                highCallNotes: row.highCallNotes,
                presenter: row.high_call_presenter,
                receiver: row.name,
            };
            this.modals.highCall = true;
        },
        deleteGospelPresentation(row) {
            this.deleteTarget = {
                type: 'gospel',
                id: row.id
            };
            this.modals.deleteConfirm = true;
        },
        deleteHighCall(row) {
            this.deleteTarget = {
                type: 'highCall',
                id: row.id
            };
            this.modals.deleteConfirm = true;
        },
        closeGospelModal() {
            this.modals.gospel = false;
            this.editMode.gospel = false;
            this.editingData = null;
        },
        closeHighCallModal() {
            this.modals.highCall = false;
            this.editMode.highCall = false;
            this.editingData = null;
        },
        confirmDelete() {
            if (!this.deleteTarget) return;

            const endpoint = this.deleteTarget.type === 'gospel'
                ? `/people/${this.deleteTarget.id}/gospel-presentation`
                : `/people/${this.deleteTarget.id}/high-call`;

            apiService.delete(endpoint)
                .then((response) => {
                    this.modals.deleteConfirm = false;
                    this.deleteTarget = null;
                    this.$message.success(response.data.message || 'Successfully deleted');
                    this.getInvitationTable();
                })
                .catch((error) => {
                    const message = error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : 'Error deleting record';
                    this.$message.error(message);
                });
        },
        show(index) {
            this.$router.push({ name: 'PersonShow', params: { id: this.people[index].id } });
        },
    },
    watch: {
        selectedGpFilters(newVals) {
            // Reset conflicting options but allow multiple filters from different categories
            if (newVals.includes('gp_received_yes') && newVals.includes('gp_received_no')) {
                const last = newVals[newVals.length - 1];
                this.selectedGpFilters = newVals.filter((v) => !v.startsWith('gp_received_')).concat(last);
                return;
            }
            if (newVals.includes('gp_response_yes') && newVals.includes('gp_response_no')) {
                const last = newVals[newVals.length - 1];
                this.selectedGpFilters = newVals.filter((v) => !v.startsWith('gp_response_')).concat(last);
                return;
            }

            // Map to filters
            if (newVals.includes('gp_received_yes')) {
                this.filters.receivedGp = 'yes';
            } else if (newVals.includes('gp_received_no')) {
                this.filters.receivedGp = 'no';
            } else {
                this.filters.receivedGp = null;
            }

            if (newVals.includes('gp_response_yes')) {
                this.filters.gpResponse = 'Yes';
            } else if (newVals.includes('gp_response_no')) {
                this.filters.gpResponse = 'No';
            } else {
                this.filters.gpResponse = null;
            }

            // Reset to page 1 and refetch with new filters
            this.$store.commit('People/Pagination/setCurrentPage', 1);
            this.getInvitationTable();
        },
        selectedHcFilters(newVals) {
            // Reset conflicting options
            if (newVals.includes('hc_received_yes') && newVals.includes('hc_received_no')) {
                const last = newVals[newVals.length - 1];
                this.selectedHcFilters = newVals.filter((v) => !v.startsWith('hc_received_')).concat(last);
                return;
            }
            const hcResp = ['hc_response_green', 'hc_response_yellow', 'hc_response_red'];
            if (hcResp.filter((v) => newVals.includes(v)).length > 1) {
                const last = newVals[newVals.length - 1];
                this.selectedHcFilters = newVals.filter((v) => !hcResp.includes(v)).concat(last);
                return;
            }

            // Map to filters
            if (newVals.includes('hc_received_yes')) {
                this.filters.receivedHc = 'yes';
            } else if (newVals.includes('hc_received_no')) {
                this.filters.receivedHc = 'no';
            } else {
                this.filters.receivedHc = null;
            }

            if (newVals.includes('hc_response_green')) {
                this.filters.hcResponse = 'Green';
            } else if (newVals.includes('hc_response_yellow')) {
                this.filters.hcResponse = 'Yellow';
            } else if (newVals.includes('hc_response_red')) {
                this.filters.hcResponse = 'Red';
            } else {
                this.filters.hcResponse = null;
            }

            // Reset to page 1 and refetch with new filters
            this.$store.commit('People/Pagination/setCurrentPage', 1);
            this.getInvitationTable();
        },
        selectedInvolvements(newVal) {
            // Map to filters - single value or null
            this.filters.involvement = newVal || null;
            this.$store.commit('People/Pagination/setCurrentPage', 1);
            this.getInvitationTable();
        },
        dateFilterType(newVal, oldVal) {
            // Clear date range when date type is cleared, and reload if there was an active filter
            if (!newVal && oldVal) {
                const hadDateRange = this.dateRange && this.dateRange.length === 2;
                this.dateRange = null;
                // Only reload if there was a date range that we're clearing
                if (hadDateRange) {
                    this.$store.commit('People/Pagination/setCurrentPage', 1);
                    this.getInvitationTable();
                }
            }
        },
        // Sync local searchQuery with pagination store
        paginationSearchQuery(newVal) {
            if (this.searchQuery !== newVal) {
                this.searchQuery = newVal;
            }
        }
    },
    created() {
        // Initialize search query from pagination store if it exists
        if (this.$store.state.People.Pagination.searchQuery) {
            this.searchQuery = this.$store.state.People.Pagination.searchQuery;
        }
        this.getInvitationTable();
    },
    beforeDestroy() {
        // todo destroy the state.
    },
};
</script>

<style>

.button--gospel {
  color: #FFF;
  background-color: #0B3B60;
  border-color: #0B3B60;
}

.button--gospel:hover {
  color: #FFF;
  background-color: #3A5CAC;
  border-color: #3A5CAC;
}

.button--highCall {
  color: #FFF;
  background-color: #66B1E6;
  border-color: #66B1E6;
}

.button--highCall:hover {
  color: #FFF;
  background-color: #9bc5f8;
  border-color: #9bc5f8;
}

.button-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 5px;
  margin-top: 0px;
}

.inline-filter-select {
  min-width: 180px;
  flex-shrink: 0;
}

.invitation-link:hover {
  text-decoration: underline;
}
</style>
